/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import type { JSX } from 'react';
import { EditorState } from 'lexical';
import { InitiativeUniversalTracker, MergedValueListOption } from './initiativeUniversalTracker';
import { FileTypes, NewEvidenceFile } from './file';
import { SafeUser, UserMin } from './user';
import { SurveyModelMinimalUtrv, UnitConfig } from './survey';
import { SurveyActionData } from './surveyAction';
import { DocumentSubType, HistoryDocument } from './document';
import UniversalTrackerClassInterface from '../model/UniversalTrackerClassInterface';
import { SubmitValueData, UniversalTrackerValuePlain, ValueData, ValueDataSource } from './universalTrackerValue';
import { OmissionReason } from '../features/survey/question/constants';
import { AiProcessingValue } from './ai';

export interface ExistingEvidenceFile extends HistoryDocument {
  status: string; // E.g: 'updated'
  type: FileTypes;
  link?: string;
  isDeleted?: boolean;
  isUpdated?: boolean;
  ownerSubType?: DocumentSubType;
  ownerId?: string;
}

export type EvidenceFile = ExistingEvidenceFile | NewEvidenceFile;

export interface DisplayCheckBox {
  [key: string]: boolean
}

export enum RowStatus {
  removed = 'removed',
  edited = 'edited',
  added = 'added',
  original = 'original',
}

export interface RowDataInfo<T = InputColumn[]> {
  id: number;
  rowStatus: RowStatus;
  isRemoved?: boolean;
  isEdited?: boolean;
  hasChanged?: boolean;
  data: T;
}

export type ValidationItem = { columnCode: string; type: 'warning' | 'error'; value: string | JSX.Element };
export type ValidationRow = { id: number, items: ValidationItem[] };

export interface TableDataInfo {
  editRowId: number,
  rows: RowDataInfo[]
}

type ValueType = number | string | undefined;
/** @deprecated replaced by types/universalTrackerValue **/
export type ValueDataObject = Record<string, ValueType>
/** @deprecated replaced by types/universalTrackerValue **/
export type ValueDataData = string | string[] | ValueDataObject

export interface PrepareSubmitDataParams {
  notApplicableType: string;
  omissionReason?: OmissionReason;
  comments: string;
  editorState?: EditorState;
  numberScale: string | undefined;
  valueData: ValueData;
  utr: UniversalTrackerClassInterface;
  unit: string | undefined;
  autoVerify: boolean;
  utrv: SurveyModelMinimalUtrv;
  files: EvidenceFile[];
  displayCheckbox: DisplayCheckBox;
  value: any;
  table: TableDataInfo;
  shouldEnforceTable?: boolean;
}

export interface InitialResettableQuestionState {
  loaded: boolean;
  displayCheckbox: DisplayCheckBox;
  displayConfirmation: boolean;
  action: string;
  saving: boolean;
  errored?: boolean;
  message?: string;
  inputMessage?: ErrorMessageType;
  comments: string; // old plain text note
  editorState?: EditorState; // rich text note
  files: NewEvidenceFile[];
  existingFiles: ExistingEvidenceFile[];
  details: { loaded: boolean, data: any };
  canContribute: boolean;
  canVerify: boolean;
  isNA: boolean;
  // @TODO: [DECIMAL] This can be set as string through valueSlider
  value: undefined | number;
  valueData: ValueData;
  table: TableDataInfo;
  /** @deprecated no longer used? */
  alternativeCode: string;
  alternativeCodeOverride: string;
  numberScale?: string,
  unit?: string,
  saveId?: string;
}

export interface AssuranceState {
  assurancePortfolio?: { assurances?: any[] };
  assurancePortfolioLoaded: boolean;
  hasPortfolio: boolean;
}

export interface QuestionReducerState extends InitialResettableQuestionState, AssuranceState {
}

export type GlossaryState = Record<string, undefined | { html: string }> | undefined;

export interface QuestionProps extends QuestionReducerState {
  // blueprint: Blueprint,
  index: number;
  hasChanged: boolean;
  utr?: UniversalTrackerClassInterface;
  utrv?: SurveyModelMinimalUtrv;
  isContributor: boolean;
  isVerifier: boolean;
  isOrganizationManager: boolean;
  survey: SurveyActionData;
  update: (data: any) => void;
  updateTable: (table: Partial<TableDataInfo>, error?: ErrorMessageType) => any;
  scrollToRef: () => void;
  glossaryState?: GlossaryState;
  // Redux
  errored?: boolean;
  isAggregate: boolean;

  handleNA?: () => void;
  handleNR?: () => void;
  handleAssuranceAction?: (action: LockAction) => Promise<void>;
  handleReject?: () => void;
  handleComments?: (comments: string) => void;
  hasValueChanged: boolean;

  addons?: Addons;
  isQuestionReadOnly?: boolean;
  initiativeUtr?: InitiativeUniversalTracker;
  subsidiaryUtr?: InitiativeUniversalTracker<MergedValueListOption>;

  /** Represent users for comments **/
  users?: UserMin[];

  enableDataHistory?: boolean;
  enableInformation?: boolean;

  onSaveCustomOption?: (code: string, columnCode?: string) => Promise<void>;
  shouldAutoOptInToWeightedAggregation?: boolean;
}

export interface QuestionState {
  errored: boolean;
  message?: string;
}

export interface QuestionChangeMinProps {
  utr: UniversalTrackerClassInterface;
  utrv: Pick<UniversalTrackerValuePlain, '_id' | 'initiativeId' | 'valueData' | 'stakeholders' | 'status'>;
}

export interface QuestionChangeOptionalProps {
  utr?: UniversalTrackerClassInterface;
  utrv?: Pick<UniversalTrackerValuePlain, '_id' | 'valueData' | 'value' | 'status'>;
  surveyData?: { unitConfig?: UnitConfig } | SurveyActionData,
}

export interface ChangeQuestionProps extends QuestionChangeMinProps {
  surveyData: Pick<SurveyActionData, '_id' | 'roles' | 'stakeholders' | 'initiativeId' | 'unitConfig' | 'workgroups'>;
  user: Pick<SafeUser, '_id' | 'permissions'>;
  initiativeUtr?: InitiativeUniversalTracker;
  shouldAutoOptInToWeightedAggregation?: boolean;
}

export interface QuestionSubmitData {
  autoVerify: 'true' | '';
  files: EvidenceFile[];
  comments: string;
  editorState?: EditorState,
  value?: string | number;
  valueData: SubmitValueData;
  unit?: string;
  numberScale?: string;
}

export interface QuestionSubmitFormData {
  autoVerify: 'true' | '';
  existingEvidence: ExistingEvidenceFile[];
  files?: File[];
  evidenceLinks?: EvidenceFile[];
  note?: string;
  editorState?: string;
  value?: string | number;
  valueData?: SubmitValueData;
  unit?: string;
  numberScale?: string;
  filesDescriptions?: string[];
  filesMetadata?: {
    id: number;
    saveToLibrary: boolean;
    description: string;
    aiProcessing?: AiProcessingValue;
  }[]
}

export enum LockAction {
  Close = 'close',
  Open = 'open',
}

// oxlint-disable-next-line no-redundant-type-constituents
export type ValueDataDataWithTable = ValueData['data'] | ValueData['table']
export type ErrorMessageType = { [key: string]: string | undefined };
export type Addon = { code: string; rowIndex?: number; element: React.ReactNode };
export type Addons = {
  before?: Addon[];
  after?: Addon[];
  belowInput?: Addon[];
}


export interface Option {
  code: string;
  name: string;
}

export interface InputColumnValue {
  value?: number | string | string[]
}
export interface InputColumn extends InputColumnValue {
  code: string;
  unit?: string;
  numberScale?: string;
  source?: ValueDataSource;
}
